import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  AppShell,
  NavLink,
  Group,
  Text,
  Stack,
  Divider,
  Avatar,
  Box,
  Burger,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconLayoutDashboard,
  IconCar,
  IconFileText,
  IconCoin,
  IconUsers,
  IconLogout,
  IconSettings,
  IconChartPie,
  IconUsersGroup,
  IconWallet,
  IconBuildingBank,
  IconTarget,
  IconArrowsExchange,
  IconArchive,
} from '@tabler/icons-react';
import './sidebar.css';

const SIDEBAR_BG = '#205357';
const SIDEBAR_WIDTH = 240;

function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [opened, { toggle, close }] = useDisclosure(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
    close();
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    if (path === '/cdg') return location.pathname === '/cdg';
    return location.pathname.startsWith(path);
  };

  const isAdmin = user?.email === 'l.simonelli@me.com';

  const sidebarContent = (
    <Stack gap={2} style={{ flex: 1 }}>
      {/* Dashboard */}
      <NavLink
        label="Dashboard"
        leftSection={<IconLayoutDashboard size={20} stroke={1.5} />}
        active={isActive('/')}
        onClick={() => { navigate('/'); close(); }}
      />

      {/* U-Save */}
      <Divider
        my={6}
        label={
          <Text size="xs" fw={600} tt="uppercase" style={{ letterSpacing: '0.5px', color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>
            U-Save
          </Text>
        }
        color="rgba(255,255,255,0.1)"
      />
      <NavLink
        label="Commissioni"
        leftSection={<IconCoin size={20} stroke={1.5} />}
        active={isActive('/commissioni-allrent')}
        onClick={() => { navigate('/commissioni-allrent'); close(); }}
      />
      <NavLink
        label="Entrate / Uscite"
        leftSection={<IconArrowsExchange size={20} stroke={1.5} />}
        active={isActive('/movimenti-usave')}
        onClick={() => { navigate('/movimenti-usave'); close(); }}
      />

      {/* Pratiche Auto */}
      <Divider
        my={6}
        label={
          <Text size="xs" fw={600} tt="uppercase" style={{ letterSpacing: '0.5px', color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>
            Pratiche Auto
          </Text>
        }
        color="rgba(255,255,255,0.1)"
      />
      <NavLink
        label="Compravendita"
        leftSection={<IconCar size={20} stroke={1.5} />}
        active={isActive('/compravendita')}
        onClick={() => { navigate('/compravendita'); close(); }}
      />
      <NavLink
        label="Noleggio"
        leftSection={<IconFileText size={20} stroke={1.5} />}
        active={isActive('/noleggio')}
        onClick={() => { navigate('/noleggio'); close(); }}
      />
      <NavLink
        label="Entrate / Uscite"
        leftSection={<IconArrowsExchange size={20} stroke={1.5} />}
        active={isActive('/movimenti-compravendita')}
        onClick={() => { navigate('/movimenti-compravendita'); close(); }}
      />
      <NavLink
        label="Compravendita (old)"
        leftSection={<IconArchive size={20} stroke={1.5} />}
        active={isActive('/pratiche-auto')}
        onClick={() => { navigate('/pratiche-auto'); close(); }}
        style={{ opacity: 0.6 }}
      />

      {/* Controllo di Gestione */}
      <Divider
        my={6}
        label={
          <Text size="xs" fw={600} tt="uppercase" style={{ letterSpacing: '0.5px', color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>
            Controllo di Gestione
          </Text>
        }
        color="rgba(255,255,255,0.1)"
      />
      <NavLink
        label="Dashboard CdG"
        leftSection={<IconChartPie size={20} stroke={1.5} />}
        active={isActive('/cdg')}
        onClick={() => { navigate('/cdg'); close(); }}
      />
      <NavLink
        label="Risorse Umane"
        leftSection={<IconUsersGroup size={20} stroke={1.5} />}
        active={isActive('/cdg/dipendenti')}
        onClick={() => { navigate('/cdg/dipendenti'); close(); }}
      />
      <NavLink
        label="Costi Personale"
        leftSection={<IconWallet size={20} stroke={1.5} />}
        active={isActive('/cdg/costi-personale')}
        onClick={() => { navigate('/cdg/costi-personale'); close(); }}
      />
      <NavLink
        label="Costi Generali"
        leftSection={<IconBuildingBank size={20} stroke={1.5} />}
        active={isActive('/cdg/costi-generali')}
        onClick={() => { navigate('/cdg/costi-generali'); close(); }}
      />
      <NavLink
        label="Impostazioni KPI"
        leftSection={<IconTarget size={20} stroke={1.5} />}
        active={isActive('/cdg/kpi')}
        onClick={() => { navigate('/cdg/kpi'); close(); }}
      />
    </Stack>
  );

  const userSection = (
    <Box>
      <Divider color="rgba(255,255,255,0.1)" mb={8} />
      {isAdmin && (
        <NavLink
          label="Utenti"
          leftSection={<IconUsers size={20} stroke={1.5} />}
          active={isActive('/utenti')}
          onClick={() => { navigate('/utenti'); close(); }}
          mb={2}
        />
      )}
      <NavLink
        label="Cambia Password"
        leftSection={<IconSettings size={20} stroke={1.5} />}
        active={isActive('/profilo')}
        onClick={() => { navigate('/profilo'); close(); }}
        mb={4}
      />
      <UnstyledButton
        className="sidebar-logout"
        onClick={handleLogout}
        style={{
          width: '100%',
          padding: '8px 12px',
          borderRadius: 8,
          color: 'rgba(255,255,255,0.5)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 14,
          transition: 'background-color 0.15s, color 0.15s',
        }}
      >
        <IconLogout size={18} stroke={1.5} />
        <Text size="sm">Esci</Text>
      </UnstyledButton>
      <Group gap={10} mt={12} px={4}>
        <Avatar size="sm" radius="xl" color="teal" variant="filled">
          {user?.nome?.charAt(0)}{user?.cognome?.charAt(0)}
        </Avatar>
        <Box style={{ flex: 1, overflow: 'hidden' }}>
          <Text size="sm" fw={500} c="white" truncate>
            {user?.nome} {user?.cognome}
          </Text>
          <Text size="xs" c="rgba(255,255,255,0.45)">{user?.ruolo}</Text>
        </Box>
      </Group>
    </Box>
  );

  return (
    <AppShell
      navbar={{
        width: SIDEBAR_WIDTH,
        breakpoint: 'sm',
        collapsed: { mobile: !opened },
      }}
      padding="lg"
    >
      {/* Sidebar */}
      <AppShell.Navbar
        style={{
          backgroundColor: SIDEBAR_BG,
          borderRight: 'none',
          display: 'flex',
          flexDirection: 'column',
          padding: '12px 8px',
        }}
        className="sidebar-nav"
      >
        {/* Branding — solo su desktop */}
        <Box mb={16} px={4} visibleFrom="sm">
          <Text size="xl" fw={800} c="white" style={{ letterSpacing: '-0.5px' }}>
            Moovo Rent
          </Text>
          <Text size="xs" c="rgba(255,255,255,0.4)" style={{ letterSpacing: '0.3px' }}>
            Gestione & KPI
          </Text>
        </Box>

        {/* Navigation */}
        <Box style={{ flex: 1, overflowY: 'auto' }}>
          {sidebarContent}
        </Box>

        {/* User section */}
        {userSection}
      </AppShell.Navbar>

      {/* Mobile topbar */}
      <Box className="mobile-topbar" hiddenFrom="sm">
        <Burger
          opened={opened}
          onClick={toggle}
          size="sm"
          color="white"
        />
        <Text size="lg" fw={800} c="white" style={{ letterSpacing: '-0.5px' }}>
          Moovo Rent
        </Text>
      </Box>

      <AppShell.Main style={{ backgroundColor: '#f8f9fa' }} className="app-main">
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}

export default Layout;
